import styled from "@emotion/styled";
import { useCallback, useMemo } from "react";

import { PROP_PANEL_PADDING, PropertiesPanelSection, Switch, toSpaceCase } from "@skilitics/designer-ui-components";
import { useFormatMessage, useFormatString, type FormatStrings } from "@skilitics/localization";
import { traverseNodeSubtree } from "@skilitics/nautilus-core-designer-data";

import { useSetMultilingualProperties, useVariantSubtree } from "../l10n";
import { useContentNode } from "../nodes/dereferenced";
import { designerNodeOrNullSelector, useNautilusSelector } from "../state";
import type { DesignerNode, DesignerNodeHandle } from "../types";

const VariantPropertyContainer = styled.li``;

const VariantPropertiesList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const VariantNode = styled.li`
  border-bottom: solid 1px ${({ theme }) => theme.outlineSoft};
  padding: 6px ${PROP_PANEL_PADDING}px;
`;

const VariantNodeList = styled.ul`
  margin: 0 -${PROP_PANEL_PADDING}px;
  padding: 0;
  list-style: none;
  color: ${({ theme }) => theme.generalTextIcon};
`;

interface Props {
  node: DesignerNodeHandle;
}

type Property = {
  name: string;
  title: FormatStrings;
  on: boolean;
  canBeToggled?: boolean;
  dependents?: string[];
};
const VariantProperty = ({
  node,
  property: { name, title, on, canBeToggled, dependents },
  properties,
}: {
  node: DesignerNode;
  property: Property;
  properties: Property[];
}) => {
  const setVariantProperties = useSetMultilingualProperties();
  const multilingualTitle = useFormatString(title);

  const handleMultilingualToggle = useCallback(() => {
    const onProperties = properties.filter((p) => p.on).map((p) => p.name);
    const propertiesToToggle = [name, ...(dependents ?? [])];

    setVariantProperties({
      nodeId: node.id,
      properties: on
        ? onProperties.filter((p) => !propertiesToToggle.includes(p))
        : [...onProperties, ...propertiesToToggle],
    });
  }, [properties, name, dependents, setVariantProperties, node.id, on]);

  return (
    <div>
      {canBeToggled && (
        <Switch label={multilingualTitle} onChange={handleMultilingualToggle} checked={on} disabled={!canBeToggled} />
      )}
    </div>
  );
};

export const Variations = (props: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const node = useNautilusSelector((state) => designerNodeOrNullSelector(state, { nodeId: props.node.id }));
  const subjectNode = useContentNode(props.node.id) || node;
  const variations = useVariantSubtree(subjectNode);

  // TEMP Disable Pinboard images from being multilingual.
  const filteredVariations = useMemo(() => {
    const disabledNodeIds: number[] = [];
    for (const variation of variations) {
      if (variation.node.typeId === "pinboard") {
        traverseNodeSubtree(
          variation.node,
          (traversedNode) => {
            if (traversedNode.typeId === "image") {
              disabledNodeIds.push(traversedNode.id);
            }
          },
          { excludeRoot: true },
        );
      }
    }
    return variations.filter((variation) => !disabledNodeIds.includes(variation.node.id));
  }, [variations]);

  if (variations.length === 0) {
    return (
      <p>
        No variations available for {toSpaceCase(props.node.typeId)}: {props.node.title}.
      </p>
    );
  }

  return (
    <PropertiesPanelSection title={formatMessage("multilingualPropertiesLabel", { nodeTitle: props.node.title })}>
      <VariantNodeList>
        {filteredVariations.map(({ node: variationNode, properties }) => (
          <VariantNode key={variationNode.id}>
            <div>
              {toSpaceCase(variationNode.typeId)}
              {variationNode.title &&
                variationNode.title.toLowerCase() !== variationNode.typeId.toLowerCase() &&
                `: ${variationNode.title}`}
            </div>
            <VariantPropertiesList>
              {properties.map((property) => (
                <VariantPropertyContainer key={property.name}>
                  <VariantProperty node={variationNode} property={property} properties={properties} />
                </VariantPropertyContainer>
              ))}
            </VariantPropertiesList>
          </VariantNode>
        ))}
      </VariantNodeList>
    </PropertiesPanelSection>
  );
};

const formatStrings = {
  multilingualPropertiesLabel: {
    en: "Multilingual Properties For {nodeTitle}",
  },
};
